import { useEffect, useState } from 'react';
import Cross from '../../../icons/Cross';
import EmailValidatedComponent from '../../../components/EmailValidatedComponent';
import { onChangePhoneNumber } from '../../../utils/phoneNumberRegex';
import colorConfigs from '../../../configs/colorConfigs';
import { useTheme } from '../../../components/hooks/useTheme';
import { Button, Dialog } from '@mui/material';
import useAuth from '../../../components/hooks/useAuth';
import { put_Address } from '../../../services/BillingAddress';
import Cookies from 'js-cookie';
import useTenant from '../../../components/hooks/useTenant';

type UpdateBillingAddressProps = {
    showPopupUpdateBillingAddress: boolean;
    setShowPopupUpdateBillingAddress: (value: boolean) => void;
    billingAddressData: any;
    setBillingAddressData: (value: any) => void;
}

const UpdateBillingAddress = ({
    showPopupUpdateBillingAddress,
    setShowPopupUpdateBillingAddress,
    billingAddressData,
    setBillingAddressData
}: UpdateBillingAddressProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [formBillingAddress, setFormBillingAddress] = useState<any>({
        name: "",
        emailAddress: "",
        phoneNumber: "",
        street: "",
        street2: "",
        zip: "",
        state: "",
        city: "",
        country: ""
    });
    const { themeStyles, theme } = useTheme();
    const textColor =
        theme === "dark"
            ? `${themeStyles.main.textSubHeadingColor}`
            : `${themeStyles.main.textHeadingColor}`;

    useEffect(() => {
        if (showPopupUpdateBillingAddress) {
            setFormBillingAddress(billingAddressData);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPopupUpdateBillingAddress])

    const closeModal = () => {
        setShowPopupUpdateBillingAddress(false);
        setFormBillingAddress({
            name: "",
            emailAddress: "",
            phoneNumber: "",
            street: "",
            street2: "",
            zip: "",
            state: "",
            city: "",
            country: ""
        });
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isProcessing) {
            setIsProcessing(true);
            put_Address({
                ...formBillingAddress,
                tenantId: tenantId
            }, auth?.id)
                .then(() => {
                    setIsProcessing(false);
                    setBillingAddressData(formBillingAddress);
                    setShowPopupUpdateBillingAddress(false);
                    handleTenantError(null);
                })
                .catch(error => {
                    handleTenantError(error);
                    setIsProcessing(false);
                })
        }
    }

    return (
        <Dialog
            open={showPopupUpdateBillingAddress}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '800px',
                    width: '100%',
                    borderRadius: '16px',
                },
            }}
        >
            <div
                className={`fixed inset-0 p-4 mt-[70px] ${showPopupUpdateBillingAddress ? 'flex' : 'hidden'} flex-wrap justify-center items-center w-full h-auto z-[1000] overflow-auto`}
            >
                <div className={`w-full max-w-[32rem] shadow-[0px_4px_6px_rgba(0,0,0,0.1)] rounded-md p-5 relative ${themeStyles.main.tailwindMainBg}`}>
                    <Cross
                        closeModal={closeModal}
                        className={`w-3 h-3 absolute top-2 right-2 cursor-pointer ${textColor}`}
                    />
                    <form onSubmit={handleSubmit}>
                        <div className="m-2 text-center">
                            <h4 className={`text-xl font-semibold ${textColor}`}>
                                Update Billing Address
                            </h4>

                            <div className="mb-4 mt-7">
                                <div className="w-full text-sm mb-4">
                                    <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>Company Name</label>
                                    <input
                                        type="text"
                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                        value={formBillingAddress.name}
                                        onChange={(e) =>
                                            setFormBillingAddress({ ...formBillingAddress, name: e.target.value })
                                        }
                                        required
                                    />
                                </div>

                                <div className="w-full text-sm mb-4">
                                    <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>Email Address</label>
                                    <EmailValidatedComponent
                                        text={formBillingAddress.emailAddress}
                                        setText={(value) => setFormBillingAddress({ ...formBillingAddress, emailAddress: value })}
                                    />
                                </div>

                                <div className="w-full text-sm mb-4">
                                    <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>Phone Number</label>
                                    <input
                                        type="text"
                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                        value={formBillingAddress.phoneNumber}
                                        onChange={(e) =>
                                            setFormBillingAddress({ ...formBillingAddress, phoneNumber: onChangePhoneNumber(e.target.value) })
                                        }
                                        required
                                    />
                                </div>

                                <div className="flex gap-4 mb-2">
                                    <div className="w-full text-sm mb-2">
                                        <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>Street</label>
                                        <input
                                            type="text"
                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            value={formBillingAddress.street}
                                            onChange={(e) =>
                                                setFormBillingAddress({ ...formBillingAddress, street: e.target.value })
                                            }
                                            required
                                        />
                                    </div>

                                    <div className="w-full text-sm mb-2">
                                        <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>Street 2</label>
                                        <input
                                            type="text"
                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            value={formBillingAddress.street2}
                                            onChange={(e) =>
                                                setFormBillingAddress({ ...formBillingAddress, street2: e.target.value })
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-4 mb-2">
                                    <div className="w-full text-sm mb-2">
                                        <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>Zip</label>
                                        <input
                                            type="text"
                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            value={formBillingAddress.zip}
                                            inputMode="numeric"
                                            onChange={(e) =>
                                                setFormBillingAddress({ ...formBillingAddress, zip: e.target.value.replace(/\D/g, "") })
                                            }
                                            required
                                        />
                                    </div>

                                    <div className="w-full text-sm mb-2">
                                        <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>State</label>
                                        <input
                                            type="text"
                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            value={formBillingAddress.state}
                                            onChange={(e) =>
                                                setFormBillingAddress({ ...formBillingAddress, state: e.target.value })
                                            }
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-4 mb-2">
                                    <div className="w-full text-sm mb-2">
                                        <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>City</label>
                                        <input
                                            type="text"
                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            value={formBillingAddress.city}
                                            onChange={(e) =>
                                                setFormBillingAddress({ ...formBillingAddress, city: e.target.value })
                                            }
                                            required
                                        />
                                    </div>

                                    <div className="w-full text-sm mb-2">
                                        <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>Country</label>
                                        <input
                                            type="text"
                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            value={formBillingAddress.country}
                                            onChange={(e) =>
                                                setFormBillingAddress({ ...formBillingAddress, country: e.target.value })
                                            }
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4 mb-4">
                                <Button variant="contained"
                                    sx={{
                                        backgroundColor: colorConfigs.btn.main.bg
                                    }}
                                    type="submit"
                                >
                                    Save
                                </Button>

                                <Button variant="contained"
                                    sx={{
                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                        border: colorConfigs.btn.cancel.border,
                                        color: colorConfigs.btn.cancel.color
                                    }}
                                    onClick={closeModal}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Dialog>
    );

}

export default UpdateBillingAddress
